// Paket P2 — Reine Client-Regeln (60_PROMOTION.md §2).
//
// Der Server entscheidet Timing/Cooldown/Inhalt; der Client entscheidet
// NUR das, wozu er schnell und ohne Netz sein muss:
//   • App-Start-Erkennung (Kaltstart + Rückkehr nach ≥ 30 min)
//   • Nachrichten-Bündelung (lokal, frühestens alle 5 Nachrichten oder
//     beim Erreichen der Schwelle — der Server addiert per `count`)
//   • Chat-Sitzungszähler je `chatId` (im Speicher; Reset beim Verlassen)
//   • Routen-Ausschluss (§2)
//   • Sitzungs-Caps (max. 1 Dialog, max. 2 Banner)
//   • 90-s-Startsperre (nicht für Intro)
//   • Preview-Modus (Caps aus)
//
// Alles als reine Funktionen mit Storage-Injektion — testbar ohne DOM,
// ohne Uhr und ohne Netz (Muster `app/reviewPrompt.ts`,
// `app/pushPrimingRules.ts`).

export const PROMO_APP_START_SESSION_KEY = 'lorath_promo_app_start_session';
export const PROMO_APP_START_LAST_HIDDEN_AT_KEY = 'lorath_promo_app_start_hidden_at';
/** 30-Min-Schwelle, ab der ein "sichtbar nach verborgen" als App-Start zählt. */
export const PROMO_APP_START_HIDDEN_THRESHOLD_MS = 30 * 60 * 1000;
export const PROMO_INTRO_LOCK_KEY = 'lorath_promo_intro_locked_session';
/** 90 s nach App-Start kein Dialog (Ausnahme: Intro). */
export const PROMO_START_LOCK_MS = 90 * 1000;

export const PROMO_MESSAGE_BUNDLE_THRESHOLD = 5;
export const PROMO_DIALOG_SESSION_CAP = 1;
export const PROMO_BANNER_SESSION_CAP = 2;

/** Wo der Dialog NICHT erscheinen darf (60_PROMOTION.md §2).
 *  Matcht exakt die Route, optional gefolgt von /, ? oder Stringende. */
export const PROMO_EXCLUDED_ROUTES: readonly RegExp[] = [
  /^\/mitgliedschaft(\/|\?|$)/,
  /^\/coins(\/|\?|$)/,
  /^\/login(\/|\?|$)/,
  /^\/onboarding(\/|\?|$)/,
  /^\/legal(\/|\?|$)/,
  /^\/admin(\/|\?|$)/,
  // W2-B/F-01: der Sofortstart eines Neulings — die erste Wahl gehoert der
  // Geschichte, nicht einem Mitgliedschafts-Dialog.
  /^\/first-scene(\/|\?|$)/,
];

export function isExcludedRoute(pathname: string): boolean {
  return PROMO_EXCLUDED_ROUTES.some((re) => re.test(pathname));
}

interface SafeStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface AppStartDetection {
  /** Sollte ein App-Start-Ereignis an den Server gemeldet werden? */
  shouldReport: boolean;
  /** true = Kaltstart (sessionStorage-Marker existiert noch nicht). */
  isColdStart: boolean;
}

function safeStorage(s: Storage | null | undefined): SafeStorage | null {
  if (!s) return null;
  return {
    getItem: (k) => { try { return s.getItem(k); } catch { return null; } },
    setItem: (k, v) => { try { s.setItem(k, v); } catch { /* noop */ } },
    removeItem: (k) => { try { s.removeItem(k); } catch { /* noop */ } },
  };
}

/**
 * Erkennt einen „App-Start". Ein Aufruf meldet TRUE genau einmal je Sitzung:
 *   • beim Kaltstart (sessionStorage noch leer), oder
 *   • nach einer Sichtbarkeitsphase von ≥ 30 min (Timestamp in localStorage
 *     je Nutzer — überlebt einen App-Restart).
 */
export function detectAppStart(deps: {
  now: number;
  sessionStorage: Storage | null;
  localStorage: Storage | null;
  userId?: string | number | null;
}): AppStartDetection {
  const session = safeStorage(deps.sessionStorage);
  const local = safeStorage(deps.localStorage);
  const userKey = String(deps.userId ?? 'anon');

  // 1. Kaltstart?
  const sessionMarker = session?.getItem(PROMO_APP_START_SESSION_KEY);
  if (!sessionMarker) {
    session?.setItem(PROMO_APP_START_SESSION_KEY, '1');
    local?.setItem(`${PROMO_APP_START_LAST_HIDDEN_AT_KEY}:${userKey}`, String(deps.now));
    return { shouldReport: true, isColdStart: true };
  }

  // 2. Aus dem Hintergrund zurück?
  const lastHiddenKey = `${PROMO_APP_START_LAST_HIDDEN_AT_KEY}:${userKey}`;
  const lastHiddenRaw = local?.getItem(lastHiddenKey);
  const lastHidden = lastHiddenRaw ? Number.parseInt(lastHiddenRaw, 10) : NaN;
  if (!Number.isFinite(lastHidden)) {
    // Kein Merker (erster Aufruf nach Login-Wechsel) → als App-Start werten.
    local?.setItem(lastHiddenKey, String(deps.now));
    return { shouldReport: true, isColdStart: false };
  }
  if (deps.now - lastHidden >= PROMO_APP_START_HIDDEN_THRESHOLD_MS) {
    local?.setItem(lastHiddenKey, String(deps.now));
    return { shouldReport: true, isColdStart: false };
  }
  return { shouldReport: false, isColdStart: false };
}

/** Wird vom visibilitychange-Listener gerufen, wenn die App in den
 *  Hintergrund geht. Speichert den Zeitstempel je Nutzer. */
export function markAppHidden(deps: {
  now: number;
  localStorage: Storage | null;
  userId?: string | number | null;
}): void {
  const local = safeStorage(deps.localStorage);
  const userKey = String(deps.userId ?? 'anon');
  local?.setItem(`${PROMO_APP_START_LAST_HIDDEN_AT_KEY}:${userKey}`, String(deps.now));
}

// ─── Nachrichten-Bündelung (lokal) ────────────────────────────────────────
export interface MessageBundleDecision {
  /** Soll der Server dieses Bündel gemeldet bekommen? */
  shouldReport: boolean;
  /** Anzahl der Nachrichten im aktuellen Bündel. */
  count: number;
  /** Neuer lokaler Zählerstand. */
  pending: number;
}

/**
 * Zählt Nachrichten lokal und meldet frühestens alle 5 (oder beim Erreichen
 * der Schwelle, falls Aufrufer die mit `thresholdHint` reicht). Die Schwelle
 * ist die `context_after`-Regel je Segment (z.B. 12 für free_new).
 */
export function accumulateMessageBundle(deps: {
  /** Wie viele Nachrichten wurden gerade gesendet? (1 oder mehr) */
  incoming: number;
  /** Bisheriger Stand des lokalen Zählers. */
  pending: number;
  /** Kontext-Schwelle je Segment (default: PROMO_MESSAGE_BUNDLE_THRESHOLD). */
  thresholdHint?: number;
}): MessageBundleDecision {
  const threshold = deps.thresholdHint ?? PROMO_MESSAGE_BUNDLE_THRESHOLD;
  const pending = Math.max(0, deps.pending) + Math.max(0, deps.incoming);
  if (pending >= threshold) {
    return { shouldReport: true, count: pending, pending: 0 };
  }
  return { shouldReport: false, count: 0, pending };
}

// ─── Chat-Sitzungszähler je chatId (im Speicher) ─────────────────────────
export interface ChatSessionMap {
  [chatId: string]: number;
}

/** Erhöht den Chat-Sitzungs-Zähler und gibt zurück, ob die Schwelle erreicht wurde. */
export function bumpChatSessionCount(
  map: ChatSessionMap,
  chatId: string,
  threshold: number,
): { next: ChatSessionMap; reached: boolean; count: number } {
  const current = Number(map[chatId] || 0) + 1;
  const next = { ...map, [chatId]: current };
  return {
    next,
    reached: current >= threshold && current === threshold,
    count: current,
  };
}

/** Setzt den Zähler zurück, wenn der Nutzer den Chat verlässt (Route wechselt). */
export function resetChatSessionCount(map: ChatSessionMap, chatId: string): ChatSessionMap {
  if (!(chatId in map)) return map;
  const next = { ...map };
  delete next[chatId];
  return next;
}

// ─── Sitzungs-Caps (max. 1 Dialog, 2 Banner) ─────────────────────────────
export interface SessionCaps {
  /** Wurde in dieser App-Sitzung schon ein Dialog gezeigt? */
  dialogShown: number;
  /** Wurden in dieser App-Sitzung schon Banner gezeigt? */
  bannerShown: number;
}

export function canShowDialog(caps: SessionCaps): boolean {
  return caps.dialogShown < PROMO_DIALOG_SESSION_CAP;
}

export function canShowBanner(caps: SessionCaps): boolean {
  return caps.bannerShown < PROMO_BANNER_SESSION_CAP;
}

export function bumpDialog(caps: SessionCaps): SessionCaps {
  return { ...caps, dialogShown: caps.dialogShown + 1 };
}

export function bumpBanner(caps: SessionCaps): SessionCaps {
  return { ...caps, bannerShown: caps.bannerShown + 1 };
}

// ─── 90-s-Startsperre (nicht für Intro) ───────────────────────────────────
export function isWithinStartLock(deps: {
  /** Wann hat die App-Session begonnen (Mount-Zeitpunkt des Hosts). */
  sessionStartedAt: number;
  now: number;
}): boolean {
  return deps.now - deps.sessionStartedAt < PROMO_START_LOCK_MS;
}

// ─── Intro-Sperre pro Sitzung (z.B. Bestandsschutz überspringt Intro) ─────
export function lockIntroThisSession(sessionStorage: Storage | null): void {
  const session = safeStorage(sessionStorage);
  session?.setItem(PROMO_INTRO_LOCK_KEY, '1');
}

export function isIntroLockedThisSession(sessionStorage: Storage | null): boolean {
  const session = safeStorage(sessionStorage);
  return session?.getItem(PROMO_INTRO_LOCK_KEY) === '1';
}

export function clearIntroLock(sessionStorage: Storage | null): void {
  const session = safeStorage(sessionStorage);
  session?.removeItem(PROMO_INTRO_LOCK_KEY);
}
